/**
 * usePhotoSelection Hook
 * Manages photo selection state with multi-select and range selection support
 * Phase 2: Gallery selection system
 */

import { useState, useCallback } from 'react';

export function usePhotoSelection<T extends { photoId: string }>(photos: T[]) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);

  // Toggle single photo selection
  const toggleSelection = useCallback((photoId: string, index: number, shiftKey: boolean = false) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);

      // Shift+click: range selection
      if (shiftKey && lastSelectedIndex !== null && index !== lastSelectedIndex) {
        const start = Math.min(lastSelectedIndex, index);
        const end = Math.max(lastSelectedIndex, index);

        // Add all photos in range
        for (let i = start; i <= end; i++) {
          if (photos[i]) {
            newSet.add(photos[i].photoId);
          }
        }
      } else {
        // Normal click: toggle single selection
        if (newSet.has(photoId)) {
          newSet.delete(photoId);
        } else {
          newSet.add(photoId);
        }
      }

      return newSet;
    });

    setLastSelectedIndex(index);
  }, [photos, lastSelectedIndex]);

  // Clear all selections
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setLastSelectedIndex(null);
  }, []);

  // Select all photos
  const selectAll = useCallback(() => {
    setSelectedIds(new Set(photos.map(p => p.photoId)));
  }, [photos]);

  // Add a batch of photo IDs to the current selection
  const selectMany = useCallback((photoIds: string[]) => {
    if (photoIds.length === 0) return;
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      photoIds.forEach((id) => newSet.add(id));
      return newSet;
    });
  }, []);

  // Deselect a batch of photo IDs from the current selection
  const deselectMany = useCallback((photoIds: string[]) => {
    if (photoIds.length === 0) return;
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      photoIds.forEach((id) => newSet.delete(id));
      return newSet;
    });
  }, []);

  // Get selected photos
  const selectedPhotos = photos.filter(p => selectedIds.has(p.photoId));

  // Check if a photo is selected
  const isSelected = useCallback((photoId: string) => {
    return selectedIds.has(photoId);
  }, [selectedIds]);

  return {
    selectedIds,
    selectedPhotos,
    selectedCount: selectedIds.size,
    toggleSelection,
    selectMany,
    deselectMany,
    clearSelection,
    selectAll,
    isSelected,
  };
}
